import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getPublicStartingList } from '@/lib/api';
import { TvQrCode } from '@/components/TvQrCode';
import type { StartingListResponse } from '@swiss-magic/shared-types';
import { getGameConfig } from '@swiss-magic/shared-types';
import { ratingFieldLabel, externalIdFieldLabel } from '@/lib/game-labels';

const CYCLE_MS = 30_000;

export function TvStartingListPage() {
  const { t } = useTranslation('tournament');
  const { t: tc } = useTranslation('common');
  const { slug } = useParams<{ slug: string }>();
  const [data, setData] = useState<StartingListResponse | null>(null);
  const [offset, setOffset] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableSectionElement>(null);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  useEffect(() => {
    if (!slug) return;
    const load = () => getPublicStartingList(slug).then(setData).catch(() => {});
    load();
    const interval = setInterval(load, 5_000);
    return () => clearInterval(interval);
  }, [slug]);

  const measureRows = useCallback(() => {
    if (!containerRef.current) return;
    const tableWrapper = containerRef.current.querySelector('.overflow-hidden');
    const firstRow = tableRef.current?.querySelector('tr');
    if (!tableWrapper || !firstRow) return;
    const available = tableWrapper.clientHeight;
    const rowHeight = firstRow.getBoundingClientRect().height || 36;
    const theadHeight = containerRef.current.querySelector('thead')?.getBoundingClientRect().height || 32;
    setRowsPerPage(Math.max(5, Math.floor((available - theadHeight) / rowHeight)));
  }, []);

  useEffect(() => {
    // Measure after data renders — try twice to handle late DOM updates
    const t1 = setTimeout(measureRows, 100);
    const t2 = setTimeout(measureRows, 500);
    window.addEventListener('resize', measureRows);
    return () => { clearTimeout(t1); clearTimeout(t2); window.removeEventListener('resize', measureRows); };
  }, [measureRows, data]);

  // Track total rows for cycling (ref avoids resetting timer on data poll)
  const totalRef = useRef(0);
  useEffect(() => {
    totalRef.current = data?.players.length ?? 0;
  }, [data]);

  // Auto-cycle through pages — depends only on rowsPerPage (not data)
  useEffect(() => {
    if (rowsPerPage >= 100) return; // not measured yet
    const timer = setInterval(() => {
      const total = totalRef.current;
      if (total <= rowsPerPage) return;
      setOffset((prev) => {
        const next = prev + rowsPerPage;
        return next >= total ? 0 : next;
      });
    }, CYCLE_MS);
    return () => clearInterval(timer);
  }, [rowsPerPage]);

  if (!data) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <p className="text-lg text-muted-foreground">{t('loading')}</p>
      </div>
    );
  }

  const config = getGameConfig(data.game);
  const showRating = config.playerFields.rating;
  const showExternalId = config.playerFields.externalId;
  const visibleRows = data.players.slice(offset, offset + rowsPerPage);
  const totalPages = Math.ceil(data.players.length / rowsPerPage);
  const currentPage = Math.floor(offset / rowsPerPage) + 1;

  return (
    <div ref={containerRef} className="h-screen bg-background p-4 flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h1 className="text-xl font-bold">{data.tournamentName}</h1>
          <p className="text-sm text-muted-foreground">{t('starting_list.subtitle')}</p>
        </div>
        {totalPages > 1 && (
          <span className="text-sm text-muted-foreground">
            {currentPage} / {totalPages}
          </span>
        )}
      </div>

      <div className="flex-1 overflow-hidden rounded border">
        <table className="w-full text-left">
          <thead className="border-b bg-muted/50">
            <tr>
              <th className="w-10 px-2 py-1.5 text-xs font-medium text-muted-foreground">{t('starting_list.table.rank')}</th>
              <th className="px-2 py-1.5 text-xs font-medium text-muted-foreground">{t('starting_list.table.name')}</th>
              {showRating && (
                <th className="w-20 px-2 py-1.5 text-xs font-medium text-muted-foreground">
                  {ratingFieldLabel(config, tc)}
                </th>
              )}
              {showExternalId && (
                <th className="w-24 px-2 py-1.5 text-xs font-medium text-muted-foreground">
                  {externalIdFieldLabel(config, tc)}
                </th>
              )}
              <th className="px-2 py-1.5 text-xs font-medium text-muted-foreground">{t('starting_list.table.club_city')}</th>
            </tr>
          </thead>
          <tbody ref={tableRef}>
            {visibleRows.map((p) => {
              const name = `${p.firstName} ${p.lastName}`;
              const clubCity = [p.club, p.city].filter(Boolean).join(', ');
              return (
                <tr key={p.startingRank} className="border-b">
                  <td className="px-2 py-1.5 text-sm text-muted-foreground">{p.startingRank}</td>
                  <td className="px-2 py-1.5 text-sm font-medium">{name}</td>
                  {showRating && (
                    <td className="px-2 py-1.5 text-sm">{p.rating ?? ''}</td>
                  )}
                  {showExternalId && (
                    <td className="px-2 py-1.5 text-sm">{p.fideId ?? ''}</td>
                  )}
                  <td className="px-2 py-1.5 text-sm text-muted-foreground">{clubCity}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {slug && <TvQrCode slug={slug} reportPath="starting-list" />}
    </div>
  );
}
